import LegalPageLayout from '../components/LegalPageLayout';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      <div className="space-y-4 text-sm leading-7 text-gray-600">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      subtitle="These draft terms govern access to Filie&apos;s private beta and set the current scope of the service."
      lastUpdated="April 13, 2026"
    >
      <Section title="Acceptance of Terms">
        <p>
          These Terms of Service ("Terms") govern your access to and use of Filie&apos;s website, applications, and
          related services (the "Service"). By using the Service, you agree to these Terms.
        </p>
        <p>
          These Terms are a working beta draft and should be reviewed by counsel before broad public launch.
        </p>
      </Section>

      <Section title="Eligibility and Accounts">
        <p>
          You must be legally able to form a binding contract to use the Service. You are responsible for maintaining
          the confidentiality of your account credentials and for activity that occurs under your account.
        </p>
        <p>
          Private beta access may require an invite code, and we may suspend or revoke access at any time.
        </p>
      </Section>

      <Section title="Current Product Scope">
        <p>
          Filie currently provides federal tax document analysis, questionnaire support, tax computation summaries,
          and draft form generation for a limited beta audience.
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Federal return analysis only.</li>
          <li>No state or local return preparation is promised at this time.</li>
          <li>No electronic filing service is currently offered.</li>
          <li>The Service may change materially during beta.</li>
        </ul>
      </Section>

      <Section title="Important Disclosures">
        <p>
          Filie is software. It is not a law firm, accounting firm, or government agency. Unless Filie expressly
          states otherwise in a signed written agreement, Filie does not provide legal representation or individualized
          professional advice.
        </p>
        <p>
          You are responsible for reviewing your documents, verifying extracted values, and determining whether any
          generated output is accurate and appropriate for filing or other use.
        </p>
      </Section>

      <Section title="User Content and Permissions">
        <p>
          You retain ownership of the documents and information you upload. You grant Filie a limited right to host,
          process, transmit, analyze, and display that content as needed to operate, secure, and improve the Service.
        </p>
        <p>
          You represent that you have the right to upload and use the information you submit.
        </p>
      </Section>

      <Section title="Prohibited Conduct">
        <ul className="list-disc space-y-2 pl-6">
          <li>Using the Service unlawfully or in a way that infringes the rights of others.</li>
          <li>Attempting to bypass access controls, security measures, or private beta restrictions.</li>
          <li>Uploading malicious code, harmful files, or content you do not have permission to use.</li>
          <li>Reverse engineering or interfering with the Service except as allowed by applicable law.</li>
        </ul>
      </Section>

      <Section title="Beta Nature of the Service">
        <p>
          The Service is offered on a beta basis and may contain bugs, outages, incomplete features, inaccurate
          outputs, or changing workflows. We may modify or discontinue features without notice.
        </p>
      </Section>

      <Section title="Disclaimers">
        <p>
          The Service is provided "as is" and "as available" to the fullest extent permitted by law. Filie disclaims
          warranties of merchantability, fitness for a particular purpose, non-infringement, accuracy, and availability.
        </p>
      </Section>

      <Section title="Limitation of Liability">
        <p>
          To the fullest extent permitted by law, Filie and its officers, employees, and affiliates will not be liable
          for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits,
          revenues, goodwill, data, or tax benefits arising from or related to the Service.
        </p>
        <p>
          To the fullest extent permitted by law, Filie&apos;s total liability for claims arising out of or related to the
          Service will not exceed the greater of $100 or the amount you paid Filie in the 12 months before the claim arose.
        </p>
      </Section>

      <Section title="Termination">
        <p>
          You may stop using the Service at any time. We may suspend or terminate access at any time, including for
          security, abuse prevention, or beta program management reasons.
        </p>
      </Section>

      <Section title="Governing Law">
        <p>
          These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles,
          except where applicable law requires otherwise.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about these Terms can be sent to
          {' '}<a className="text-gray-900 underline" href="mailto:info@filie.ai">info@filie.ai</a>.
        </p>
      </Section>
    </LegalPageLayout>
  );
}
